import { useQuery } from "@tanstack/react-query";
import createPrivateAdminPanelInstance from "../../api/PrivateAdminPanelInstance";
import { API_ROUTES } from "../../api/ApiRoutes";
import { bonusAdminBody } from "./bonusAdminBody";
import type { DynamicBonusCatalog } from "../../components/Promotions/DynamicBonuses/types";

type StoredToken = { AccessToken?: string };

export const FREESPIN_CATALOG_PROVIDERS = ["bgaming", "alea", "threeoaks"] as const;
export const EMPTY_FREESPIN_MAP_STALE_MS = 30_000;
export const COMPLETE_FREESPIN_MAP_STALE_MS = 1000 * 60 * 5;

const providerMapEmpty = (
  maps: Record<string, string[]> | undefined,
  provider: string,
): boolean => !Array.isArray(maps?.[provider]) || maps[provider].length === 0;

/** True when any authorable provider is missing keys. */
export const freeSpinCurrenciesIncomplete = (
  catalog?: DynamicBonusCatalog | null,
): boolean => {
  const maps = catalog?.free_spin_currencies;
  if (!maps) return true;
  return FREESPIN_CATALOG_PROVIDERS.some((provider) =>
    providerMapEmpty(maps, provider),
  );
};

/** True when no authorable provider has keys (create is fully blocked). */
export const freeSpinCurrenciesAllEmpty = (
  catalog?: DynamicBonusCatalog | null,
): boolean => {
  const maps = catalog?.free_spin_currencies;
  if (!maps) return true;
  return FREESPIN_CATALOG_PROVIDERS.every((provider) =>
    providerMapEmpty(maps, provider),
  );
};

export const catalogFreshness = (catalog?: DynamicBonusCatalog | null) => {
  const allEmpty = freeSpinCurrenciesAllEmpty(catalog);
  return {
    staleTime: allEmpty ? EMPTY_FREESPIN_MAP_STALE_MS : COMPLETE_FREESPIN_MAP_STALE_MS,
    // Recover a fully blocked catalog on tab focus / remount. Do not poll,
    // and do not refetch on focus just because one tenant lacks a provider.
    refetchOnWindowFocus: allEmpty,
    refetchInterval: false as const,
  };
};

async function fetchDynamicBonusCatalog(): Promise<DynamicBonusCatalog> {
  const tokenString = localStorage.getItem("token");
  const token: StoredToken | null = tokenString ? JSON.parse(tokenString) : null;
  const instance = createPrivateAdminPanelInstance(token?.AccessToken);
  const response = await instance.get(API_ROUTES.DYNAMIC_BONUS_CATALOG);
  const catalog = bonusAdminBody<DynamicBonusCatalog>(response);
  if (!catalog) {
    throw new Error("Dynamic bonus catalog unavailable");
  }
  return catalog;
}

export const DYNAMIC_BONUS_CATALOG_QUERY_KEY = ["DYNAMIC_BONUS_CATALOG"] as const;

export const useDynamicBonusCatalog = () => {
  return useQuery<DynamicBonusCatalog>({
    queryKey: DYNAMIC_BONUS_CATALOG_QUERY_KEY,
    queryFn: fetchDynamicBonusCatalog,
    staleTime: (query) => catalogFreshness(query.state.data).staleTime,
    refetchOnWindowFocus: (query) =>
      catalogFreshness(query.state.data).refetchOnWindowFocus,
    refetchInterval: (query) => catalogFreshness(query.state.data).refetchInterval,
  });
};
